import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';

export interface FullScreenCoverProps {
  open: boolean;
  onClose: () => void;
  /** Shown in the header bar next to the close button. */
  title: ReactNode;
  /** Right-aligned header content — links, buttons, status chips. */
  headerActions?: ReactNode;
  /**
   * `false` (default): the stage centres + pads its child (read-only artifacts).
   * `true`: the stage is a bare flex column so an embedded editor fills it.
   */
  bare?: boolean;
  children: ReactNode;
  /** Accessible label for the dialog. Falls back to a string `title`. */
  ariaLabel?: string;
  'data-testid'?: string;
  closeTestId?: string;
}

// Matches --duration-slide (220ms) with headroom so the node unmounts only
// after the slide-down completes.
const EXIT_MS = 260;

/**
 * Full-screen cover surface that slides up from the bottom over the still-mounted
 * page (portal + `pn-cover` shell + Esc-to-close). Generalises the mockup
 * `ProjectArtifactCover` / `EditorCover` chrome.
 *
 * Presence: the node stays mounted through the slide-down so the exit animates —
 * callers should keep `<FullScreenCover>` mounted and toggle `open` (rather than
 * conditionally rendering it) to get the exit transition.
 *
 * Esc closes the cover unless focus is inside an editable surface within it
 * (input / textarea / contenteditable) — those keep Esc for their own menus.
 */
export function FullScreenCover({
  open,
  onClose,
  title,
  headerActions,
  bare = false,
  children,
  ariaLabel,
  'data-testid': testId,
  closeTestId
}: FullScreenCoverProps) {
  // `rendered` keeps the portal mounted through the exit animation; `active`
  // drives the `data-open` attribute the CSS transitions on.
  const [rendered, setRendered] = useState(open);
  const [active, setActive] = useState(false);
  const exitTimer = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Mount on open; on close, slide down then unmount after the transition.
  useEffect(() => {
    if (open) {
      if (exitTimer.current) {
        clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setRendered(true);
      return undefined;
    }
    setActive(false);
    exitTimer.current = window.setTimeout(() => {
      setRendered(false);
      exitTimer.current = null;
    }, EXIT_MS);
    return undefined;
  }, [open]);

  // Once the panel is committed to the DOM (rendered), flip `data-open` to true
  // on the next frame so the transition plays. The cover instance persists
  // across open/close cycles (the host keeps it mounted to animate the exit),
  // so on every open we must force the browser to resolve the
  // `data-open="false"` start state — read `offsetHeight` — before flipping to
  // `true`, or the transition won't re-fire after the first open. useLayoutEffect
  // runs post-commit, so `panelRef` is guaranteed present here.
  useLayoutEffect(() => {
    if (!rendered || !open) return undefined;
    if (panelRef.current) void panelRef.current.offsetHeight;
    rafRef.current = requestAnimationFrame(() => setActive(true));
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [rendered, open]);

  useEffect(
    () => () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const el = document.activeElement as HTMLElement | null;
      const editable =
        !!el &&
        (el.isContentEditable ||
          el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT');
      if (editable) return; // yield Esc to the focused editor surface
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!rendered) return null;

  const label = ariaLabel ?? (typeof title === 'string' ? title : undefined);

  return createPortal(
    <div
      ref={panelRef}
      className="pn-cover"
      data-open={active ? 'true' : 'false'}
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className="pn-cover-bar">
        <button
          type="button"
          className="pn-cover-x"
          onClick={onClose}
          data-testid={closeTestId}
          aria-label="Close"
        >
          ✕
        </button>
        <span className="pn-cover-title">{title}</span>
        {headerActions ? (
          <div className="pn-cover-actions">{headerActions}</div>
        ) : null}
      </div>
      <div className={bare ? 'pn-cover-stage-bare' : 'pn-cover-stage'}>
        {children}
      </div>
    </div>,
    document.body
  );
}
